import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';
import { Link } from 'react-router-dom';

interface FilterRequest {
  id: string;
  campaign_id: string;
  request_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  filters: any;
  target_lead_count: number;
  actual_lead_count?: number;
  created_at: string;
  campaigns?: {
    name: string;
    status: string;
  };
  companies?: {
    name: string;
  };
  users?: {
    full_name: string;
    email: string;
  };
}

export default function CSRFilterRequests() {
  const [filterRequests, setFilterRequests] = useState<FilterRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<FilterRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    fetchFilterRequests();
  }, [statusFilter, priorityFilter]);

  const fetchFilterRequests = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('preset_filter_requests')
        .select(`
          *,
          campaigns:campaign_id (
            name,
            status
          ),
          companies:company_id (
            name
          ),
          users:user_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      if (priorityFilter !== 'all') {
        query = query.eq('priority', priorityFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setFilterRequests(data || []);
    } catch (error) {
      console.error('Error fetching filter requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      console.log(`Updating request ${requestId} to status: ${newStatus}`);
      
      // First, get the current user to ensure we're authorized
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user?.id, user?.email);
      
      // Check if this request has a company_id (required for RLS)
      const { data: request, error: fetchError } = await supabase
        .from('preset_filter_requests')
        .select('company_id, user_id')
        .eq('id', requestId)
        .single();
        
      if (fetchError) {
        console.error('Failed to fetch request:', fetchError);
        alert('Failed to fetch request details');
        return;
      }
      
      console.log('Request company_id:', request?.company_id);
      if (!request?.company_id) {
        console.warn('Request has no company_id - this may cause RLS issues');
      }
      
      const updateData: any = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'in_progress') {
        updateData.started_at = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('preset_filter_requests')
        .update(updateData)
        .eq('id', requestId)
        .select();

      if (error) {
        console.error('Database error:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        alert(`Failed to update status: ${error.message}\n\nThis may be due to permissions. Check if you're assigned to this company.`);
        throw error;
      }

      console.log('Update successful:', data);
      await fetchFilterRequests();
    } catch (error) {
      console.error('Error updating request status:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'normal': return 'text-blue-600 bg-blue-50';
      case 'low': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const renderFiltersPreview = (filters: any) => {
    const categories = Object.keys(filters || {});
    const totalFilters = categories.reduce((count, cat) => {
      const subFilters = filters[cat];
      const activeSubFilters = Object.values(subFilters || {}).filter(v => 
        Array.isArray(v) ? v.length > 0 : v
      ).length;
      return count + activeSubFilters;
    }, 0);

    return (
      <div className="text-sm text-gray-600">
        {totalFilters} filters across {categories.length} categories
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Target Lead Requests</h1>
          <Link
            to="/csr-dashboard"
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Back to Dashboard
          </Link>
        </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority Filter
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Request Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company / User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Campaign
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Target Leads
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Filters
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filterRequests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {request.request_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{request.companies?.name || 'No company'}</div>
                  <div className="text-sm text-gray-500">
                    {request.users?.full_name || 'Unknown user'}
                    {request.users?.email && (
                      <span className="text-xs text-gray-400 ml-1">({request.users.email})</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{request.campaigns?.name || 'No campaign'}</div>
                  {request.campaigns?.status && (
                    <div className="text-xs text-gray-500">Status: {request.campaigns.status}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(request.priority)}`}>
                    {request.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                    {request.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {request.target_lead_count || 'N/A'}
                  {request.actual_lead_count && (
                    <div className="text-xs text-gray-500">
                      Actual: {request.actual_lead_count}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {renderFiltersPreview(request.filters)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(request.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                    {request.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Starting processing for:', request.id);
                          updateRequestStatus(request.id, 'in_progress');
                        }}
                        className="text-green-600 hover:text-green-900 font-medium"
                      >
                        Start Processing
                      </button>
                    )}
                    {request.status === 'in_progress' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Completing request:', request.id);
                          updateRequestStatus(request.id, 'completed');
                        }}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filterRequests.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No filter requests found</p>
          </div>
        )}
      </div>

      {/* Filter Details Modal - Updated design to match system modals */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold">Filter Request Details</h2>
                    <button
                      onClick={() => setSelectedRequest(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Request Name</h3>
                    <p className="text-gray-900">{selectedRequest.request_name}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Campaign</h3>
                    <p className="text-gray-900">{selectedRequest.campaigns?.name || 'No campaign'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Company</h3>
                    <p className="text-gray-900">{selectedRequest.companies?.name || 'No company'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">User</h3>
                    <p className="text-gray-900">{selectedRequest.users?.full_name || 'Unknown user'}</p>
                    <p className="text-sm text-gray-500">{selectedRequest.users?.email}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Priority</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(selectedRequest.priority)}`}>
                      {selectedRequest.priority}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Status</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Target Lead Count</h3>
                    <p className="text-gray-900">{selectedRequest.target_lead_count || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Created</h3>
                    <p className="text-gray-900">{new Date(selectedRequest.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-700 mb-3">Filter Criteria</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {Object.entries(selectedRequest.filters || {}).map(([category, filters]) => (
                      <div key={category} className="mb-4">
                        <h4 className="font-medium text-gray-900 capitalize mb-2">{category}</h4>
                        <div className="ml-4 space-y-1">
                          {Object.entries(filters as any).map(([key, value]) => {
                            if (!value || (Array.isArray(value) && value.length === 0)) return null;
                            return (
                              <div key={key} className="text-sm">
                                <span className="font-medium text-gray-700 capitalize">
                                  {key.replace('_', ' ')}:
                                </span>{' '}
                                <span className="text-gray-600">
                                  {Array.isArray(value) ? value.join(', ') : value}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                  </div>
                </div>
                
                {/* Modal Footer */}
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  {selectedRequest.status === 'pending' && (
                    <button
                      onClick={() => {
                        updateRequestStatus(selectedRequest.id, 'in_progress');
                        setSelectedRequest(null);
                      }}
                      className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                    >
                      Start Processing Request
                    </button>
                  )}
                  {selectedRequest.status === 'in_progress' && (
                    <button
                      onClick={() => {
                        updateRequestStatus(selectedRequest.id, 'completed');
                        setSelectedRequest(null);
                      }}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    >
                      Mark as Complete
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}